/**
 * Implements a styled dropdown list
 */
import { useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import Text from '../../atoms/Text';
import Image from '../../atoms/Image';
import useDevice from '../../pages/MainPage/useMobileDetect';

import './styles.scss';

interface IListProps {
  className: string;
  children: any;
}

/**
 * List holder
 * @param props
 * @returns
 */
const List = (props: IListProps) => (
  <div className={`dropdown__list ${props.className}`}>{props.children}</div>
);

interface IItemProps {
  children: React.ReactChild | React.ReactChild[];
}
/**
 * List item
 */
const Item = (props: IItemProps) => (
  <div className='dropdown__list__item'>{props.children}</div>
);

export interface ICustomSelectProps {
  options: { value: string; label: string; image?: string }[];
  disabledOptions?: string[];
  disabledReason?: string;
  onSelect: (value: string) => void;
  initial: { value: string; label: string; image?: string };
  width?: number | string;
  height?: number | string;
  gap?: number;
}

const CustomSelect = ({
  options,
  disabledOptions,
  disabledReason,
  onSelect,
  initial,
  width,
  height,
  gap = 8,
}: ICustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initial);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useDevice();

  const handleSelect = (option: { value: string; label: string }) => {
    onSelect(option.value);
    setSelected(option);
    setIsOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (!containerRef.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      className='select'
      style={{ width, minWidth: width, height, minHeight: height }}
      ref={containerRef}
    >
      <div
        className='select__selected'
        onClick={() => setIsOpen(!isOpen)}
        style={{ padding: `0 ${gap}px` }}
      >
        <Text size={14}>{selected.label}</Text>
        {selected.image && (
          <Image
            size='m'
            src={selected.image}
            alt={selected.label}
            className='select__image'
          />
        )}
        <div
          style={{ right: gap }}
          className={`icon-chevron-down select__arrow ${
            isOpen ? 'select__arrow--active' : ''
          }`}
        />
      </div>
      <div className={`select__items ${isOpen ? '' : 'select__items--hide'}`}>
        {options.map((option) => (
          <div
            key={option.label}
            onClick={() =>
              !disabledOptions?.includes(option.value)
                ? handleSelect(option)
                : undefined
            }
            style={{ paddingLeft: gap, paddingRight: gap }}
            className={`select__items__option ${
              selected.value === option.value
                ? 'select__items__option--active'
                : ''
            } ${
              disabledOptions?.includes(option.value)
                ? 'select__items__option--disabled'
                : ''
            }`}
            {...(disabledOptions?.includes(option.value) && !!disabledReason
              ? {
                  'data-tip': disabledReason,
                  'data-for': 'dropdown-tooltip',
                }
              : {})}
          >
            <Text size={14}>{option.label}</Text>
            {selected.image && (
              <Image
                size='m'
                src={option.image}
                alt={option.label}
                className='select__image'
              />
            )}
          </div>
        ))}
        {!!disabledOptions?.length && !!disabledReason && (
          <ReactTooltip
            id='dropdown-tooltip'
            place='left'
            effect='solid'
            className='text_tooltip'
            event={isMobile ? 'click' : undefined}
          />
        )}
      </div>
    </div>
  );
};

export { List, Item, CustomSelect };
